import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import api from '../utils/api';
import { AppPageShell, PageHeader } from './common/ui';
import ReCAPTCHA from "react-google-recaptcha";

const RequestDemoForm = ({ onNotification }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    companyName: "",
    role: "",
    useCase: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [throttled, setThrottled] = useState(false);
  const [throttleTimeRemaining, setThrottleTimeRemaining] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  // Check for throttling on component mount
  useEffect(() => {
    checkThrottleStatus();
    
    // Detect if the user is on a mobile device
    const checkMobile = () => {
      // Check screen width
      const isMobileWidth = window.innerWidth < 768;
      
      // Check user agent for mobile devices
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      
      setIsMobile(isMobileWidth || isMobileDevice);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Countdown for throttled submissions
  useEffect(() => {
    let interval;
    if (throttled && throttleTimeRemaining > 0) {
      interval = setInterval(() => {
        setThrottleTimeRemaining(prev => {
          if (prev <= 1) {
            setThrottled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [throttled, throttleTimeRemaining]);

  const checkThrottleStatus = async () => {
    try {
      const response = await api.request('/request-demo/throttle-status', {
        method: 'GET'
      });
      
      if (response.throttled) {
        setThrottled(true);
        setThrottleTimeRemaining(response.timeRemaining || 3600);
      }
    } catch (error) {
      console.error("Error checking throttle status:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  // Handle CAPTCHA expiration
  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    onNotification('info', 'CAPTCHA verification expired. Please verify again.');
  };

  const validateEmail = (email) => {
    // First check basic format
    const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicRegex.test(email)) {
      return { valid: false, message: 'Please enter a valid email address.' };
    }
    
    // Split the email into parts
    const [localPart, domain] = email.split('@');
    
    // Check for suspicious patterns in the local part (random strings, numbers only, etc.)
    if (/^[0-9]+$/.test(localPart)) {
      return { valid: false, message: 'Invalid email format. Please use a business email.' };
    }
    
    if (/^[a-zA-Z0-9]{8,}$/.test(localPart) && !localPart.includes('.')) {
      return { valid: false, message: 'This email format appears suspicious. Please use your business email.' };
    }
    
    // Check domain part
    const commonPersonalDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 
      'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
      'gmx.com', 'live.com', 'msn.com', 'me.com', 'mail.ru', 'inbox.com'
    ];
    
    // Temporary/disposable email domains to block
    const disposableDomains = [
      'temp-mail.org', 'guerrillamail.com', 'mailinator.com', 'tempmail.com', 
      'temp-mail.com', '10minutemail.com', 'yopmail.com', 'throwawaymail.com', 
      'vulca.sbs', 'sharklasers.com', 'trashmail.com', 'getairmail.com',
      'dispostable.com', 'temp.plus', 'fake-mail.com', 'fakeinbox.com'
    ];
    
    if (commonPersonalDomains.includes(domain.toLowerCase())) {
      return { valid: false, message: 'Please use your business email address.' };
    }
    
    if (disposableDomains.some(d => domain.toLowerCase().includes(d))) {
      return { valid: false, message: 'We do not accept disposable or temporary email addresses.' };
    }
    
    // Valid email
    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (throttled) {
      onNotification('error', `Too many requests. Please try again in ${Math.ceil(throttleTimeRemaining / 60)} minutes.`);
      return;
    }
    
    setIsSubmitting(true);

    // Advanced email validation
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      onNotification('error', emailValidation.message);
      setIsSubmitting(false);
      return;
    }

    // Phone number validation
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      onNotification('error', 'Please enter a valid phone number in international format (e.g., +1234567890).');
      setIsSubmitting(false);
      return;
    }

    // Check for captcha
    if (!captchaToken) {
      onNotification('error', 'Please complete the captcha verification.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Collect browser fingerprinting and user behavioral data
      const deviceData = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        colorDepth: window.screen.colorDepth,
        deviceMemory: navigator.deviceMemory,
        hardwareConcurrency: navigator.hardwareConcurrency,
        platform: navigator.platform,
      };

      const response = await api.request('/request-demo', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          captchaToken,
          deviceData
        })
      });
      
      // Check if the response status is success
      if (response && response.status === "success") {
        // Reset form
        setFormData({
          name: "",
          email: "",
          phoneNumber: "",
          companyName: "",
          role: "",
          useCase: "",
        });
        setCaptchaToken(null);
        
        // Reset the reCAPTCHA
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }

        // Redirect to success page
        navigate('/demo-request-success');
      } else {
        // Handle unexpected response format
        onNotification('error', response.message || "Unexpected response from server.");
        setIsSubmitting(false);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Oops! Something went wrong. Please try again.";
      
      if (error.response?.data?.message?.includes("already requested")) {
        onNotification('info', errorMessage);
      } else if (error.response?.data?.message?.includes("throttled")) {
        setThrottled(true);
        setThrottleTimeRemaining(error.response?.data?.timeRemaining || 3600);
        onNotification('error', `Too many requests. Please try again later.`);
      } else if (error.response?.data?.message?.includes("email has already been used")) {
        // Handle duplicate email error specifically
        onNotification('info', "This email has already been used for a demo request. Please use a different business email or contact our support team for assistance.");
      } else if (error.response?.data?.message?.includes("valid business email address with an active domain")) {
        // Handle invalid domain error
        onNotification('error', "We couldn't verify your email domain. Please use an email from a business with an active website.");
      } else if (error.response?.data?.message?.includes("Invalid captcha")) {
        // Handle invalid captcha specifically
        onNotification('error', "Captcha verification failed. Please try again.");
        setCaptchaToken(null);
        // Reset the reCAPTCHA widget
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } else {
        onNotification('error', errorMessage);
      }
      setIsSubmitting(false);
    }
  };

  const inputClasses = `
    w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200
    focus:border-purple-500 focus:ring-2 focus:ring-purple-200 
    transition-all duration-200 ease-in-out
    placeholder-gray-400 text-gray-800
  `;

  const labelClasses = `
    block text-sm font-medium text-gray-700 mb-1
  `;

  if (throttled) {
    return (
      <div className="p-6 bg-red-50 rounded-lg border border-red-200 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Demo Request Limit Reached</h2>
        <p className="text-gray-700 mb-4">
          For security reasons, we've temporarily limited demo requests from your location.
        </p>
        <p className="text-gray-700">
          {throttleTimeRemaining >= 86400 ? (
            `Please try again in ${Math.floor(throttleTimeRemaining / 86400)} days`
          ) : throttleTimeRemaining >= 3600 ? (
            `Please try again in ${Math.floor(throttleTimeRemaining / 3600)} hours`
          ) : throttleTimeRemaining >= 60 ? (
            `Please try again in ${Math.floor(throttleTimeRemaining / 60)} minutes`
          ) : (
            `Please try again in ${throttleTimeRemaining} seconds`
          )}
        </p>
      </div>
    );
  }

  return (
    <AppPageShell maxWidthClass="max-w-3xl">
      <PageHeader
        eyebrow="Admin"
        title="Demo requests"
        description="Review inbound demo form submissions and verification status."
      />
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelClasses}>
              Name
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className={inputClasses}
                required
                minLength={2}
                maxLength={50}
              />
            </label>
          </div>

        <div>
          <label className={labelClasses}>
            Email
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@company.com"
              className={inputClasses}
              required
            />
            <p className="text-xs text-gray-700 mt-1">
              Please use your business email address - we can only process one submission per email (choose wisely!)
            </p>
          </label>
        </div>

        <div>
          <label className={labelClasses}>
            Phone Number
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="+1 (555) 000-0000"
              className={inputClasses}
              required
              pattern="^\+[1-9]\d{1,14}$"
              title="Please enter a valid phone number in international format (e.g., +1234567890)"
            />
          </label>
        </div>

        <div>
          <label className={labelClasses}>
            Company Name
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Company Inc."
              className={inputClasses}
              required
              minLength={2}
              maxLength={100}
            />
          </label>
        </div>
      </div>

      <div>
        <label className={labelClasses}>
          Role
          <select
            name="role"
            value={formData.role}
            onChange={handleChange}
            className={inputClasses}
            required
          >
            <option value="">Select your role</option>
            <option value="ceo">CEO/President</option>
            <option value="coo">COO</option>
            <option value="cto">CTO</option>
            <option value="cfo">CFO</option>
            <option value="cmo">CMO</option>
            <option value="vp_sales">VP of Sales</option>
            <option value="vp_marketing">VP of Marketing</option>
            <option value="vp_operations">VP of Operations</option>
            <option value="vp_technology">VP of Technology</option>
            <option value="sales_director">Sales Director</option>
            <option value="sales_manager">Sales Manager</option>
            <option value="marketing_director">Marketing Director</option>
            <option value="marketing_manager">Marketing Manager</option>
            <option value="hr_director">HR Director</option>
            <option value="hr_manager">HR Manager</option>
            <option value="it_director">IT Director</option>
            <option value="it_manager">IT Manager</option>
            <option value="product_director">Product Director</option>
            <option value="product_manager">Product Manager</option>
            <option value="operations_manager">Operations Manager</option>
            <option value="customer_success_manager">Customer Success Manager</option>
            <option value="customer_service_manager">Customer Service Manager</option>
            <option value="business_analyst">Business Analyst</option>
            <option value="data_scientist">Data Scientist</option>
            <option value="developer">Software Developer</option>
            <option value="data_analyst">Data Analyst</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>

      <div>
        <label className={labelClasses}>
          Tell us about your use case
          <textarea
            name="useCase"
            value={formData.useCase}
            onChange={handleChange}
            placeholder="What are you looking to achieve with our AI call center solution?"
            className={`${inputClasses} h-32 resize-none`}
            required
            minLength={20}
            maxLength={1000}
          ></textarea>
        </label>
      </div>
      
      <div className="overflow-x-auto py-2">
        <div className={`flex justify-center my-4 ${isMobile ? 'scale-[0.85] origin-center' : ''}`}>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            onExpired={handleCaptchaExpired}
            size={isMobile ? "compact" : "normal"}
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-4">
        <p className="text-sm text-gray-900">
          * Required fields
        </p>
        <button
          type="submit"
          disabled={isSubmitting || !captchaToken}
          className={`
            relative inline-flex items-center justify-center px-8 py-3 
            bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2]
            text-white font-semibold rounded-lg
            transform transition-all duration-200
            hover:scale-[1.02] hover:shadow-lg
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E852FF]
            disabled:opacity-70 disabled:cursor-not-allowed
          `}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <Oval
                height={20}
                width={20}
                color="#fff"
                visible={true}
                ariaLabel="oval-loading"
                secondaryColor="#fefefe"
                strokeWidth={4}
                strokeWidthSecondary={4}
              />
              <span className="ml-2">Submitting...</span>
            </span>
          ) : (
            "Request Demo"
          )}
        </button>
      </div>
      </form>
    </AppPageShell>
  );
};

export default RequestDemoForm;
